import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { Template } from '@/api/templates';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, LoadingState } from '@/components/ui';
import { filterTemplatesByChannel } from '@/config/channels';
import { DispatchModeBadge } from '@/features/templates/dispatchMode';
import { getTechnicalTypeLabel } from '@/features/templates/templateTaxonomy';
import { useClearClusterTriggerBinding, useClusterTriggerBindings, useSetClusterTriggerBinding } from '@/hooks/useServiceClusters';
import { useTemplates } from '@/hooks/useTemplates';
import { useAuthStore } from '@/store/authStore';

interface ClusterTriggerBindingsCardProps {
  clusterId: string;
  title?: string;
  description?: string;
  defaultCollapsed?: boolean;
  onBindingsChanged?: () => void | Promise<void>;
}

export function ClusterTriggerBindingsCard({
  clusterId,
  title,
  description,
  defaultCollapsed = true,
  onBindingsChanged,
}: ClusterTriggerBindingsCardProps) {
  const { getEffectiveTenantId } = useAuthStore();
  const tenantId = getEffectiveTenantId();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [savingByKey, setSavingByKey] = useState<Record<string, boolean>>({});
  const bindingsQuery = useClusterTriggerBindings(clusterId);
  const { templates } = useTemplates(tenantId || undefined);
  const setBinding = useSetClusterTriggerBinding();
  const clearBinding = useClearClusterTriggerBinding();
  const isLegacyFallback = bindingsQuery.data?.is_legacy_fallback === true;

  const bindingOptions = useMemo(
    () =>
      templates
        .filter((template) => template.category !== 'manual')
        .sort((a, b) => {
          if (a.is_active !== b.is_active) {
            return a.is_active ? -1 : 1;
          }
          return a.name.localeCompare(b.name, 'ru');
        }),
    [templates]
  );

  const getBindingKey = (triggerType: string, channel?: string) => `${clusterId}:${triggerType}:${channel || 'generic'}`;

  const getBindingOptions = (channel?: string): Template[] => {
    if (!channel) {
      return bindingOptions;
    }
    return filterTemplatesByChannel(bindingOptions, channel);
  };

  const setSaving = (triggerType: string, channel: string | undefined, value: boolean) => {
    const key = getBindingKey(triggerType, channel);
    setSavingByKey((prev) => ({ ...prev, [key]: value }));
  };

  const resolveTemplateMeta = (templateId?: string, fallbackName?: string, fallbackIsActive?: boolean) => {
    const matchedTemplate = templates.find((template) => template.id === templateId);
    return {
      name: fallbackName || matchedTemplate?.name || templateId || '—',
      isActive: matchedTemplate?.is_active ?? fallbackIsActive ?? true,
    };
  };

  const handleSetBinding = async (
    triggerType: 'after_completed' | 'after_cancelled' | 'manual',
    templateId: string,
    channel?: string
  ) => {
    if (!templateId) return;
    try {
      setSaving(triggerType, channel, true);
      await setBinding.mutateAsync({ clusterId, triggerType, templateId, channel });
      await bindingsQuery.refetch();
      await onBindingsChanged?.();
    } finally {
      setSaving(triggerType, channel, false);
    }
  };

  const handleClearBinding = async (triggerType: 'after_completed' | 'after_cancelled' | 'manual', channel?: string) => {
    try {
      setSaving(triggerType, channel, true);
      await clearBinding.mutateAsync({ clusterId, triggerType, channel });
      await bindingsQuery.refetch();
      await onBindingsChanged?.();
    } finally {
      setSaving(triggerType, channel, false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>{title || 'Привязки Cluster Follow-up'}</CardTitle>
          {description !== '' && (
            <p className="text-sm text-neutral-600">
              {description || 'Generic-привязка и channel overrides для cluster follow-up теперь на той же модели, что и event-driven pipeline.'}
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsCollapsed((prev) => !prev)}>
          {isCollapsed ? <ChevronRight className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
          {isCollapsed ? 'Показать' : 'Скрыть'}
        </Button>
      </CardHeader>
      {!isCollapsed && (
        <CardContent>
          {!tenantId ? (
            <EmptyState type="search" title="Выберите клинику" description="Для настройки cluster-trigger привязок выберите клинику." />
          ) : bindingsQuery.isLoading ? (
            <LoadingState />
          ) : (
            <div className="space-y-3">
              {isLegacyFallback && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  На этом backend ещё нет `trigger-bindings` API. Показан совместимый read-only режим по старым cluster-template связям.
                </div>
              )}
              {(bindingsQuery.data?.bindings || []).map((binding) => {
                const genericBinding = binding.generic_binding || {
                  label: 'Общая generic-привязка',
                  has_binding: false,
                  mapping_active: false,
                };
                const genericKey = getBindingKey(binding.trigger_type, genericBinding.channel);
                const genericSaving = !!savingByKey[genericKey];
                const genericTemplateMeta = resolveTemplateMeta(
                  genericBinding.template?.id,
                  genericBinding.template?.name,
                  genericBinding.template?.is_active
                );

                return (
                  <div key={binding.trigger_type} className="space-y-3 rounded-md border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="info">{binding.label}</Badge>
                      <DispatchModeBadge mode={binding.dispatch_mode || (binding.trigger_type === 'manual' ? 'manual' : undefined)} />
                      <span className="font-mono text-xs text-neutral-500">{binding.trigger_type}</span>
                    </div>

                    <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">{genericBinding.label}</Badge>
                        {genericBinding.template ? (
                          <span className={`text-xs ${genericTemplateMeta.isActive ? 'text-green-700' : 'text-amber-700'}`}>
                            Текущий: {genericTemplateMeta.name} {genericTemplateMeta.isActive ? '(активен)' : '(неактивен)'}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-700">Текущий: не назначен</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          className="min-w-[360px] rounded-md border border-neutral-300 px-3 py-2 text-sm"
                          value={genericBinding.template?.id || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) return;
                            handleSetBinding(binding.trigger_type, value, genericBinding.channel);
                          }}
                          disabled={genericSaving || isLegacyFallback}
                        >
                          <option value="">Выберите generic шаблон</option>
                          {getBindingOptions(genericBinding.channel).map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} [{getTechnicalTypeLabel(template)}] {template.is_active ? '' : ' (неактивен)'}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={genericSaving || isLegacyFallback || !genericBinding.has_binding}
                          onClick={() => handleClearBinding(binding.trigger_type, genericBinding.channel)}
                        >
                          Очистить
                        </Button>
                      </div>
                      {genericBinding.warning && <p className="text-xs text-amber-700">{genericBinding.warning}</p>}
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      {(binding.channel_bindings || []).map((slot) => {
                        const saving = !!savingByKey[getBindingKey(binding.trigger_type, slot.channel)];
                        const slotTemplateMeta = resolveTemplateMeta(
                          slot.template?.id,
                          slot.template?.name,
                          slot.template?.is_active
                        );
                        return (
                          <div key={`${binding.trigger_type}:${slot.channel}`} className="space-y-2 rounded-md border border-neutral-200 p-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary">{slot.label}</Badge>
                              {slot.template ? (
                                <span className={`text-xs ${slotTemplateMeta.isActive ? 'text-green-700' : 'text-amber-700'}`}>
                                  Override: {slotTemplateMeta.name} {slotTemplateMeta.isActive ? '(активен)' : '(неактивен)'}
                                </span>
                              ) : (
                                <span className="text-xs text-neutral-500">Override не задан</span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                className="min-w-[280px] rounded-md border border-neutral-300 px-3 py-2 text-sm"
                                value={slot.template?.id || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (!value) return;
                                  handleSetBinding(binding.trigger_type, value, slot.channel);
                                }}
                                disabled={saving || isLegacyFallback}
                              >
                                <option value="">Выберите override</option>
                                {getBindingOptions(slot.channel).map((template) => (
                                  <option key={template.id} value={template.id}>
                                    {template.name} [{getTechnicalTypeLabel(template)}] {template.is_active ? '' : ' (неактивен)'}
                                  </option>
                                ))}
                              </select>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={saving || isLegacyFallback || !slot.has_binding}
                                onClick={() => handleClearBinding(binding.trigger_type, slot.channel)}
                              >
                                Очистить
                              </Button>
                            </div>
                            {slot.warning && <p className="text-xs text-amber-700">{slot.warning}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
